import React, { useState, useEffect } from 'react';
import { X, Edit2, Save, XCircle } from 'lucide-react';
import { doSignOut } from '../../Firebase/auth';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import './ProfileModal.css';

interface VolunteerProfile {
  id: number;
  firstname: string;
  surname: string;
  email: string;
  phone: string;
  location: string;
  availability: string;
  skills: string[];
  application_status: string;
  created_at: string;
}

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { volunteerId, userRole } = useAuth();
  const [profile, setProfile] = useState<VolunteerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSkills, setEditedSkills] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState('');

  const navigate = useNavigate();

const handleLogout = async () => {
  try {
    await doSignOut();
    onClose(); // Close the modal
    navigate('/login');
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

  useEffect(() => {
    if (isOpen && volunteerId) {
      fetchProfile();
    }
  }, [isOpen, volunteerId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/volunteers/${volunteerId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }
      
      const data = await response.json();
      setProfile(data);
      setEditedSkills(data.skills || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !editedSkills.includes(newSkill.trim())) {
      setEditedSkills([...editedSkills, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setEditedSkills(editedSkills.filter(skill => skill !== skillToRemove));
  };

  const handleSaveSkills = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/volunteers/${volunteerId}/skills`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ skills: editedSkills })
      });

      if (!response.ok) {
        throw new Error('Failed to update skills');
      }

      setProfile(prev => prev ? { ...prev, skills: editedSkills } : null);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update skills');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal-content" onClick={e => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h2>My Profile</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading profile...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : profile ? (
          <div className="profile-details">
            <div className="profile-section">
              <h3>Personal Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>First Name:</label>
                  <span>{profile.firstname}</span>
                </div>
                <div className="info-item">
                  <label>Surname:</label>
                  <span>{profile.surname}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{profile.email}</span>
                </div>
                <div className="info-item">
                  <label>Phone:</label>
                  <span>{profile.phone}</span>
                </div>
                <div className="info-item">
                  <label>Location:</label>
                  <span>{profile.location}</span>
                </div>
                <div className="info-item">
                  <label>Availability:</label>
                  <span>{profile.availability}</span>
                </div>
                <div className="info-item">
                  <label>Status:</label>
                  <span className={`status-badge ${profile.application_status}`}>
                    {profile.application_status}
                  </span>
                </div>
                <div className="info-item">
                  <label>Member Since:</label>
                  <span>{new Date(profile.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="profile-section">
              <div className="skills-header">
                <h3>Skills</h3>
                {!isEditing ? (
                  <button 
                    className="edit-button"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit2 size={16} />
                    Edit Skills
                  </button>
                ) : (
                  <button 
                    className="save-button"
                    onClick={handleSaveSkills}
                  >
                    <Save size={16} />
                    Save
                  </button>
                )}
              </div>
              
              {isEditing ? (
                <div className="skills-editor">
                  <div className="skills-input">
                    <input
                      type="text"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="Add a new skill"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                    />
                    <button onClick={handleAddSkill}>Add</button>
                  </div>
                  <div className="skills-list editing">
                    {editedSkills.map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                        <button onClick={() => handleRemoveSkill(skill)}>
                          <XCircle size={14} />
                        </button>
                      </span>
                    ))}
                    {editedSkills.length === 0 && (
                      <p className="no-skills">No skills added yet</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="skills-list">
                  {profile.skills && profile.skills.length > 0 ? (
                    profile.skills.map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="no-skills">No skills listed</p>
                  )}
                </div>
                           
              )}
            </div>
               <div className="profile-modal-footer">
         <button 
             onClick={handleLogout}
              className="logout-button"
               aria-label="Log out"
              >
         <LogOut size={16} />
               Log Out
        </button>
        <button className="close-button" onClick={onClose}>
            <X size={20} />
       </button>
     </div>
          </div>
        ) : (
          <div className="no-profile">No profile data found</div>
        )}
      </div>
    </div>
  );
}